import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Clock, User, Mail, MessageSquare, CheckCircle2, Loader2, Phone, Sparkles, MapPin, Globe } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { usStates, countries } from "@/data/locations";
import { supabase } from "@/integrations/supabase/client";

const bookingSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().optional(),
  country: z.string().min(1, "Please select a country"),
  state: z.string().optional(),
  preferredDate: z.string().min(1, "Please select a date"),
  preferredTime: z.string().min(1, "Please select a time"),
  topic: z.string().min(1, "Please select a topic"),
  message: z.string().optional(),
});

interface BookCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const timeSlots = [
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
];

const topics = [
  "Admin Support",
  "Business Documentation",
  "Market Research",
  "Client Coordination",
  "Process Improvement",
  "General Inquiry",
];

export function BookCallDialog({ open, onOpenChange }: BookCallDialogProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    preferredDate: "",
    preferredTime: "",
    topic: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateStep = (currentStep: number) => {
    try {
      if (currentStep === 1) {
        z.object({
          name: z.string().min(2, "Name must be at least 2 characters"),
          email: z.string().email("Please enter a valid email"),
          country: z.string().min(1, "Please select a country"),
        }).parse({ name: formData.name, email: formData.email, country: formData.country });
      } else if (currentStep === 2) {
        z.object({
          preferredDate: z.string().min(1, "Please select a date"),
          preferredTime: z.string().min(1, "Please select a time"),
          topic: z.string().min(1, "Please select a topic"),
        }).parse({
          preferredDate: formData.preferredDate,
          preferredTime: formData.preferredTime,
          topic: formData.topic,
        });
      }
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    setIsSubmitting(true);
    
    try {
      // Build location string
      const locationParts = [];
      if (formData.state) locationParts.push(formData.state);
      const countryName = countries.find(c => c.value === formData.country)?.label || formData.country;
      locationParts.push(countryName);
      const location = locationParts.join(", ");

      const { error } = await supabase
        .from("call_bookings")
        .insert({
          name: formData.name,
          email: formData.email,
          contact: formData.phone || null,
          location: location,
          preferred_date: formData.preferredDate,
          preferred_time: formData.preferredTime,
          topic: formData.topic,
          message: formData.message || null,
        });

      if (error) throw error;
      
      setIsSuccess(true);
      
      toast.success("Call scheduled successfully!", {
        description: "You will receive a confirmation email shortly.",
      });

      // Reset after showing success
      setTimeout(() => {
        onOpenChange(false);
        setStep(1);
        setIsSuccess(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          country: "",
          state: "",
          preferredDate: "",
          preferredTime: "",
          topic: "",
          message: "",
        });
      }, 2000);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to schedule call", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(1);
      setIsSuccess(false);
      setErrors({});
    }, 300);
  };

  // Generate next 14 days for date selection
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push({
          value: date.toISOString().split("T")[0],
          label: date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          }),
        });
      }
    }
    return dates;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-border overflow-hidden">
        {/* Progress indicator */}
        {!isSuccess && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-muted">
            <motion.div
              className="h-full bg-gradient-to-r from-accent to-accent-glow"
              initial={{ width: "33%" }}
              animate={{ width: step === 1 ? "33%" : step === 2 ? "66%" : "100%" }}
              transition={{ duration: 0.3 }}
            />
          </div>
        )}

        <AnimatePresence mode="wait">
          {isSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="py-12 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6"
              >
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </motion.div>
              <h3 className="text-2xl font-bold text-foreground mb-2">Call Scheduled!</h3>
              <p className="text-muted-foreground">
                Check your email for confirmation details.
              </p>
            </motion.div>
          ) : (
            <>
              <DialogHeader className="pt-4">
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <div className="p-2 rounded-lg bg-gradient-sky">
                    <Calendar className="h-5 w-5 text-accent-foreground" />
                  </div>
                  Schedule a Call
                </DialogTitle>
                <DialogDescription>
                  {step === 1
                    ? "Tell us about yourself"
                    : "Choose your preferred time"}
                </DialogDescription>
              </DialogHeader>

              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-5 py-4"
              >
                {step === 1 && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className={errors.name ? "border-destructive" : ""}
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">{errors.name}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        Phone (Optional)
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                      />
                    </div>

                    {/* Country Selection */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        Country *
                      </Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) =>
                          setFormData({ ...formData, country: value, state: "" })
                        }
                      >
                        <SelectTrigger className={errors.country ? "border-destructive" : ""}>
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border max-h-60">
                          {countries.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.country && (
                        <p className="text-sm text-destructive">{errors.country}</p>
                      )}
                    </div>

                    {/* State Selection (only for US) */}
                    {formData.country === "US" && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          State
                        </Label>
                        <Select
                          value={formData.state}
                          onValueChange={(value) =>
                            setFormData({ ...formData, state: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select your state" />
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border max-h-60">
                            {usStates.map((state) => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        Preferred Date
                      </Label>
                      <Select
                        value={formData.preferredDate}
                        onValueChange={(value) =>
                          setFormData({ ...formData, preferredDate: value })
                        }
                      >
                        <SelectTrigger className={errors.preferredDate ? "border-destructive" : ""}>
                          <SelectValue placeholder="Select a date" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {getAvailableDates().map((date) => (
                            <SelectItem key={date.value} value={date.value}>
                              {date.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.preferredDate && (
                        <p className="text-sm text-destructive">{errors.preferredDate}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        Preferred Time
                      </Label>
                      <Select
                        value={formData.preferredTime}
                        onValueChange={(value) =>
                          setFormData({ ...formData, preferredTime: value })
                        }
                      >
                        <SelectTrigger className={errors.preferredTime ? "border-destructive" : ""}>
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.preferredTime && (
                        <p className="text-sm text-destructive">{errors.preferredTime}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-muted-foreground" />
                        Topic
                      </Label>
                      <Select
                        value={formData.topic}
                        onValueChange={(value) =>
                          setFormData({ ...formData, topic: value })
                        }
                      >
                        <SelectTrigger className={errors.topic ? "border-destructive" : ""}>
                          <SelectValue placeholder="What would you like to discuss?" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {topics.map((topic) => (
                            <SelectItem key={topic} value={topic}>
                              {topic}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.topic && (
                        <p className="text-sm text-destructive">{errors.topic}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        Additional Notes (Optional)
                      </Label>
                      <Textarea
                        id="message"
                        placeholder="Anything specific you'd like to discuss?"
                        value={formData.message}
                        onChange={(e) =>
                          setFormData({ ...formData, message: e.target.value })
                        }
                        rows={3}
                      />
                    </div>
                  </>
                )}
              </motion.div>

              <div className="flex gap-3 pt-2">
                {step > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => setStep(step - 1)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                )}
                {step < 2 ? (
                  <Button
                    variant="hero"
                    onClick={handleNext}
                    className="flex-1"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="hero"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <Calendar className="h-4 w-4" />
                        Schedule Call
                      </>
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
